// src/components/Gallery.tsx

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useGalleryAPI, DriveImage } from "@/hooks/useGalleryAPI";
import { Lightbox } from "@/components/Lightbox";

export const Gallery = () => {
  const { loading, error, getImagesByCategory, getCategories } = useGalleryAPI();
  
  // Map backend category names to frontend display names
  const getCategoryDisplayName = (category: string) => {
    switch (category) {
      case 'Birthday':
        return 'Kids';
      case 'Special Occasions':
        return 'Celebrations';
      case 'Cupcakes and More':
        return 'Cupcakes & more';
      default:
        return category;
    }
  };
  
  // Reorder categories for UI display
  const getOrderedCategories = () => {
    const allCategories = getCategories();
    const desiredOrder = ['All', 'Wedding', 'Sculpted', 'Birthday', 'Special Occasions', 'Cupcakes and More'];
    
    // Filter and reorder based on desired sequence, only including categories that exist
    return desiredOrder.filter(category => allCategories.includes(category));
  };
  
  const categories = getOrderedCategories();
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentImages, setCurrentImages] = useState<DriveImage[]>([]);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  // Update URL and handle deep linking
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCategory = params.get('category');
    const urlImage = params.get('image');
    
    if (urlCategory && categories.includes(urlCategory)) {
      setActiveCategory(urlCategory);
    }
    
    if (urlImage && categories.length > 0) {
      const categoryImages = getImagesByCategory(urlCategory || activeCategory);
      const imageIndex = parseInt(urlImage);
      if (!isNaN(imageIndex) && imageIndex >= 0 && imageIndex < categoryImages.length) {
        setCurrentImages(categoryImages);
        setCurrentImageIndex(imageIndex);
        setLightboxOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, getImagesByCategory, activeCategory]);

  // Update current images when category changes and set first image
  useEffect(() => {
    if (!loading && !error) {
      const categoryImages = getImagesByCategory(activeCategory);
      setCurrentImages(categoryImages);
      setCurrentImageIndex(0);
    }
  }, [activeCategory, getImagesByCategory, loading, error]);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    
    // Update URL
    const params = new URLSearchParams(window.location.search);
    if (category === 'All') {
      params.delete('category');
    } else {
      params.set('category', category);
    }
    params.delete('image');
    
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.pushState(null, '', newUrl);
  };

  const handleMainImageClick = () => {
    setLightboxOpen(true);
    
    // Update URL
    const params = new URLSearchParams(window.location.search);
    if (activeCategory !== 'All') {
      params.set('category', activeCategory);
    }
    params.set('image', currentImageIndex.toString());
    
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  const handlePrevious = useCallback(() => {
    const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : currentImages.length - 1;
    setCurrentImageIndex(newIndex);
  }, [currentImageIndex, currentImages.length]);

  const handleNext = useCallback(() => {
    const newIndex = currentImageIndex < currentImages.length - 1 ? currentImageIndex + 1 : 0;
    setCurrentImageIndex(newIndex);
  }, [currentImageIndex, currentImages.length]);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  const handleLightboxClose = () => {
    setLightboxOpen(false);
    
    // Update URL to remove image parameter
    const params = new URLSearchParams(window.location.search);
    params.delete('image');
    
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.pushState(null, '', newUrl);
  };

  const handleLightboxNavigate = (index: number) => {
    setCurrentImageIndex(index);
    
    // Update URL
    const params = new URLSearchParams(window.location.search);
    if (activeCategory !== 'All') {
      params.set('category', activeCategory);
    }
    params.set('image', index.toString());
    
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  const filteredItems = getImagesByCategory(activeCategory);
  const currentImage = currentImages[currentImageIndex];

  return (
    <section id="gallery" className="py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="mb-6 text-[var(--heading-color)]">
            Our Gallery
          </h2>
          <p className="subheading max-w-3xl mx-auto mb-4 text-[var(--heading-color)]/80">PORTFOLIO OF CUSTOM CREATIONS</p>
          <p className="max-w-3xl mx-auto leading-relaxed">
            Explore our portfolio of custom creations, each one unique and crafted with passion. From intimate celebrations to grand events, see how we bring visions to life.
          </p>
        </div>

        {/* Category Tabs (Gallery) */}
        <div className="mb-16 md:mb-12 bg-transparent">
          <div
            className="grid w-full max-w-4xl mx-auto grid-cols-3 lg:grid-cols-6 gap-2 bg-transparent p-0 pb-2"
            style={
              {
                // Factory fallbacks (Awards legacy palette)
                // active: #e6a4a4 (white), inactive: #cc8f8f (f5f5f5)
                ['--tab-active-bg' as any]: 'var(--gallery-tab-active-bg, #e6a4a4)',
                ['--tab-active-text' as any]: 'var(--gallery-tab-active-text, #ffffff)',
                ['--tab-inactive-bg' as any]: 'var(--gallery-tab-inactive-bg, #cc8f8f)',
                ['--tab-inactive-text' as any]: 'var(--gallery-tab-inactive-text, #f5f5f5)',
              } as React.CSSProperties
            }
          >
            {categories.map((category) => {
              const isActive = category === activeCategory;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleCategoryChange(category)}
                  className="px-4 py-3 text-sm font-medium transition rounded-[6px] border-0 shadow-none"
                  style={{
                    backgroundColor: isActive ? 'var(--tab-active-bg)' : 'var(--tab-inactive-bg)',
                    color:           isActive ? 'var(--tab-active-text)' : 'var(--tab-inactive-text)',
                    border: '0px solid transparent',
                    boxShadow: 'none',
                    borderRadius: '6px',
                  }}
                  aria-pressed={isActive}
                >
                  {getCategoryDisplayName(category)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Extra top margin only on small screens so main image is pushed down and avoids overlap on narrow viewports */}
        <div className="mt-6 md:mt-0">
          {/* Main Image Display */}
          {loading ? (
            <div className="space-y-8">
              <Skeleton className="w-full aspect-[4/3] max-h-[600px] rounded-2xl mx-auto" />
              <div className="flex gap-4 justify-center overflow-x-auto pb-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton key={index} className="w-24 h-24 rounded-lg flex-shrink-0" />
                ))}
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                Unable to load gallery images at the moment.
              </p>
              <p className="text-sm text-muted-foreground">
                Please try again later or contact us for assistance.
              </p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                No images yet in {activeCategory === 'All' ? 'the gallery' : `the ${getCategoryDisplayName(activeCategory)} category`}.
              </p>
              <p className="text-sm text-muted-foreground">
                Check back soon for new creations!
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Main Image */}
              <div className="relative max-w-4xl mx-auto">
                <button
                  onClick={handleMainImageClick}
                  className="group w-full overflow-hidden rounded-2xl card-shadow transition-elegant hover:scale-[1.02] focus:ring-2 focus:ring-primary focus:outline-none"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                >
                  <div className="aspect-[4/3] bg-muted">
                    <img
                      src={currentImage?.url}
                      alt={currentImage?.title}
                      className="w-full h-full object-cover transition-elegant group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                </button>

                {/* Navigation Arrows */}
                {currentImages.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handlePrevious}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white/20 focus:ring-2 focus:ring-white bg-black/30 backdrop-blur-sm opacity-80 hover:opacity-100 transition-opacity"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleNext}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white/20 focus:ring-2 focus:ring-white bg-black/30 backdrop-blur-sm opacity-80 hover:opacity-100 transition-opacity"
                      aria-label="Next image"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </Button>
                  </>
                )}
              </div>

              {/* Thumbnail Strip */}
              <div className="flex gap-4 justify-center overflow-x-auto pb-4 px-4 sm:px-0">
                {currentImages.map((image, index) => (
                  <button
                    key={image.filename}
                    onClick={() => handleThumbnailClick(index)}
                    className={`flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden transition-elegant hover:scale-105 focus:ring-2 focus:ring-primary focus:outline-none ${
                      index === currentImageIndex
                        ? 'ring-2 ring-primary scale-105'
                        : 'opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lightbox */}
        <Lightbox
          images={currentImages}
          currentIndex={currentImageIndex}
          isOpen={lightboxOpen}
          onClose={handleLightboxClose}
          onNavigate={handleLightboxNavigate}
        />

        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-6">
            Ready to create your own masterpiece?
          </p>
          <Button
            size="lg"
            onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            className="
              bg-[var(--gallery-cta-bg)]
              text-[var(--gallery-cta-text)]
              hover:bg-[var(--gallery-cta-hover-bg)]
              hover:text-[var(--gallery-cta-hover-text)]
              font-semibold px-8 py-4 rounded-xl elegant-shadow transition-elegant
            "
          >
            Start Your Custom Order
          </Button>
        </div>
      </div>
    </section>
  );
};
